/**
 * Offline photo/video upload queue.
 *
 * When a tech captures a photo while offline (no signal), the File blob +
 * its metadata (customer_id, folder_id, parent_type, parent_id) are stashed
 * in a dedicated IndexedDB store. On reconnect we drain the queue by
 * calling the regular presign→PUT→finalize flow.
 *
 * Design notes:
 *   • Separate store from the generic API-call queue in offlineQueue.js
 *     because binary Files are too large for the mutation queue's 10 KB
 *     batches and we need a dedicated sync strategy.
 *   • We key by ULID-ish string (timestamp + random) so drain order is
 *     upload order.
 *   • We subscribe to `online` events and drain automatically. Callers can
 *     also force-drain via `drainMediaQueue()`.
 *   • Items that fail 5 times in a row are marked `stuck` and surfaced in
 *     the sync indicator panel so the user can discard them manually.
 */
import { uploadMedia } from "./mediaR2";

const DB = "hv-offline";
const STORE = "media-queue";
const DB_VERSION = 2; // bumped — schema changed

const listeners = new Set();

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      // Keep compatibility with offlineQueue.js store
      if (!db.objectStoreNames.contains("queue")) {
        db.createObjectStore("queue", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode = "readonly") {
  const db = await openDB();
  return db.transaction(STORE, mode).objectStore(STORE);
}

function notify() {
  listeners.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

export function subscribeMediaQueue(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function getMediaQueueCount() {
  const s = await tx();
  return new Promise((resolve) => {
    const req = s.count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(0);
  });
}

export async function getMediaQueueEntries() {
  const s = await tx();
  return new Promise((resolve) => {
    const req = s.getAll();
    req.onsuccess = () => resolve((req.result || []).sort((a, b) => a.created_at - b.created_at));
    req.onerror = () => resolve([]);
  });
}

/** Queue a single File for later upload. Returns the queue entry id. */
export async function queueMediaUpload(file, meta = {}) {
  const s = await tx("readwrite");
  const id = `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const entry = {
    id,
    file, // browsers persist Blob refs across sessions if the file is stored via IndexedDB (Chrome/Firefox do this)
    filename: file.name || "upload",
    size: file.size,
    content_type: file.type || "application/octet-stream",
    meta,
    created_at: Date.now(),
    attempts: 0,
    last_error: null,
    stuck: false,
  };
  await new Promise((resolve) => {
    const req = s.add(entry);
    req.onsuccess = resolve;
    req.onerror = resolve; // if dup id, ignore
  });
  notify();
  return id;
}

export async function discardMediaEntry(id) {
  const s = await tx("readwrite");
  await new Promise((resolve) => {
    const req = s.delete(id);
    req.onsuccess = resolve;
    req.onerror = resolve;
  });
  notify();
}

export async function clearMediaQueue() {
  const s = await tx("readwrite");
  await new Promise((resolve) => {
    const req = s.clear();
    req.onsuccess = resolve;
    req.onerror = resolve;
  });
  notify();
}

let draining = false;

/**
 * Attempt to upload every queued item. Stops on the first network failure
 * and keeps the remaining items in the queue for the next attempt.
 */
export async function drainMediaQueue() {
  if (draining) return { drained: 0, remaining: await getMediaQueueCount() };
  draining = true;
  let drained = 0;
  try {
    if (!navigator.onLine) {
      return { drained: 0, remaining: await getMediaQueueCount() };
    }
    const entries = await getMediaQueueEntries();
    for (const entry of entries) {
      if (entry.stuck) continue;
      try {
        await uploadMedia(entry.file, entry.meta);
        await discardMediaEntry(entry.id);
        drained++;
      } catch (e) {
        const s = await tx("readwrite");
        const attempts = (entry.attempts || 0) + 1;
        await new Promise((resolve) => {
          const req = s.put({
            ...entry,
            attempts,
            last_error: e?.message || String(e),
            stuck: attempts >= 5,
          });
          req.onsuccess = resolve;
          req.onerror = resolve;
        });
        notify();
        // Stop draining — don't hammer a failing network.
        if (!navigator.onLine) break;
      }
    }
  } finally {
    draining = false;
  }
  return { drained, remaining: await getMediaQueueCount() };
}

/** Subscribe to `online` so the queue drains automatically. */
export function installAutoDrain() {
  if (typeof window === "undefined") return;
  if (window.__hv_media_drain_installed) return;
  window.__hv_media_drain_installed = true;
  const run = () => drainMediaQueue();
  window.addEventListener("online", run);
  // Also try once on app boot (in case we were offline when the tab was
  // closed and are online now).
  setTimeout(run, 1500);
}
